<template>
  <Card v-bind="$attrs" v-on="$listeners" class="shops-list">
    <template #default>
      <StatusFilter v-model="filter.hideCompleted" :label="$t('hideAccepted')" storageKey="shops:filter" />
      <div class="shops-list__scroller">
        <DataTable
          ref="table"
          v-bind="dataTableProps"
          sticky
          @update:selected="$ev => (selected = $ev)"
          :value="items"
          theme="border"
          :columns="[
            { id: 'number', label: $t('number') },
            { id: 'status', label: $t('status'), size: '1fr' },
            { id: 'owners', label: $tc('owner', 2) },
            { id: 'delivery-log', label: '' },
            {
              id: 'total',
              label: `${$t('totalPrice')}<span class='fade' style='margin-left: 4px'>(${$t('includingVat')})</span>`,
              align: 'end',
            },
          ]"
          :row-wrapper="
            item => ({
              component: 'router-link',
              props: {
                to: { name: 'residential-edit', params: { id: item.id, step: 'shop' } },
                style: 'color: inherit',
              },
            })
          "
        >
          <template #number="{ item }">{{ item.number }}</template>
          <template #status="{ item }">
            <Badge v-if="!item.shop" :value="$t('projectShopsEditShop')" type="outlined" />
            <SignerStatus v-else :value="item" />
          </template>
          <template #owners="{ item }">
            <Avatar :value="getOwners($path('shop.contract', item))" size="small" />
          </template>
          <template #delivery-log="{ item }">
            <DeliiveryLogsShopClick :value="$path('shop.contract', item)" />
          </template>
          <template #total="{ item }">
            <span :class="{ fade: !getTotal(item) }">{{ getTotal(item) || '-' }}</span>
          </template>
          <template #primary-row-actions="{ item }">
            <ButtonComponent
              tag="router-link"
              v-tooltip="$t('dashboard')"
              :icon="['fal', 'columns']"
              :to="{ name: 'residential-edit', params: { id: item.id, step: 'shop' } }"
            />
            <ButtonComponent
              v-if="$path('shop.url', item)"
              v-tooltip="$t('shopLinkPresentation')"
              :icon="['fal', 'eye']"
              tag="a"
              :href="$path('shop.url', item)"
              target="_blank"
            />
            <ButtonComponent
              v-if="
                $store.getters['Project/hasRole']('shop.manager') &&
                $path('shop.url', item) &&
                !['accepted', 'signed'].includes($path('shop.contract.status', item))
              "
              tag="router-link"
              v-tooltip="$t('edit')"
              :icon="['fal', 'pen']"
              :to="{ name: 'shop-mutate', params: { id: $path('shop.id', item) } }"
            />
          </template>
          <template #secondary-row-actions="{ item, visible }">
            <Actions
              v-if="visible && $path('shop.id', item)"
              placement="shop"
              :query="{ shop: $path('shop.id', item) }"
              type="list"
            />
            <hr />
          </template>
          <template #select-actions>
            <ButtonComponent
              :label="$t('setDueDates')"
              :icon="['fal', 'calendar']"
              @click="openSetDates"
              :promise="promises.setDates"
            />
            <ButtonComponent
              :label="$t('startShops')"
              :icon="['fad', 'calendar']"
              @click="startShopContracts"
              :disabled="startShopsState.disabled"
              v-tooltip.right="{
                content: startShopsState.tooltip,
                boundariesElement: body,
                classes: 'shops-list__tooltip',
              }"
              :promise="promises.startShops"
              @onSuccess="reset()"
            />
          </template>
        </DataTable>
        <InfiniteLoading v-if="project.id" @infinite="next" :identifier="infiniteId">
          <template #spinner>
            <div class="infinite-loading-spinner">
              <span class="infinite-loading-spinner--text">{{ $t('loading', { resource: '...' }) }}</span>
              <FontAwesomeIcon
                class="infinite-loading-spinner--spinner"
                :icon="['fal', 'spinner-third']"
                spin
                size="2x"
              />
            </div>
          </template>
          <template #no-more>{{ $t('noMoreShops') }}</template>
          <template #no-results>
            <EmptyState
              :title="$t('noX', { x: $tc('unit', 2).toLowerCase() })"
              :subtitle="$t('projectShopsCreateResidentialsWarning')"
              :icon="['fal', 'home']"
            >
              <template #actions>
                <ButtonComponent
                  :label="$t('projectShopsCreateResidentials')"
                  theme="secondary"
                  :icon="['fal', 'plus']"
                  tag="router-link"
                  class="text--small"
                  :to="{
                    name: 'project-edit',
                    params: { id: project.id, step: 'residentials' },
                    query: { create: true },
                  }"
                />
              </template>
            </EmptyState>
          </template>
        </InfiniteLoading>
      </div>
      <SetDates v-if="setDates" @close="setDates = false" @done="onUpdateDone" :residentials="setDatesResidentials" />
    </template>
  </Card>
</template>

<script>
import { mapState } from 'vuex'
import { Currency } from '@/filters'
import { Initials } from '@/utils'
import API from '@/api/Residential'
import Bus from '@/bus'
import StatusFilter from '@/components/Project/StatusFilter'

import Card from '@/components/Card'
import SignerStatus from './SignerStatus'
import DeliiveryLogsShopClick from './DeliveryLogsShopClick'
import InfiniteLoading from 'vue-infinite-loading'
import SetDates from './SetDates'
import DataTable from '@/components/DataTable'
import Badge from '@/components/Badge'
import Avatar from '@/components/Avatar'
import EmptyState from '@/components/EmptyState'
import tracker from '@/tracking'
import Actions from '@/components/Actions'

const createSelection = () => ({
  allSelected: false,
  selectedIds: [],
  excludedIds: [],
  items: [],
})

export default {
  data() {
    return {
      item: {
        docs: [],
        page: null,
        pages: null,
        total: null,
      },
      infiniteId: 1,
      paginate: {
        page: 1,
        limit: 25,
      },
      noUnits: false,
      selected: createSelection(),
      setDates: false,
      setDatesResidentials: [],
      promises: {
        startShops: null,
        setDates: null,
      },
      body: document.body,
      filter: {
        hideCompleted: false,
      },
    }
  },
  watch: {
    filter: {
      handler: 'reset',
      deep: true,
    },
  },
  computed: {
    ...mapState('Project', {
      project(state) {
        return state.item
      },
    }),
    items() {
      return this.item.docs
    },
    isLast() {
      return this.item.page === this.item.pages
    },
    dataTableProps() {
      if (!this.$store.getters['Project/hasRole']('shop.manager')) return {}
      return { paginate: true, totalItems: this.item.total || undefined }
    },
    query() {
      return {
        project: this.project.id,
        ...(this.filter.hideCompleted ? { contractStatus: ['!accepted:shop'] } : {}),
      }
    },
    startShopsState() {
      const { allSelected, items } = this.selected
      const hasApplicant = e => this.$path('shop.contract.data.applicant', e)

      return {
        tooltip: items.some(e => !hasApplicant(e)) ? this.$t('noApplicantShopContractTooltip') : undefined,
        disabled:
          !this.$path('roles.shop.signingAuthority.length', this.project) ||
          (!allSelected && items.every(e => !hasApplicant(e))),
      }
    },
  },
  methods: {
    fetch() {
      if (this.item.pages === 0 || (this.item.pages && this.item.page === this.item.pages)) return Promise.resolve()
      return API.get(
        {
          paginate: this.paginate,
          filter: this.query,
        },
        {
          query: 'ProjectShops',
        },
      ).then(data => {
        tracker.emit('ProjectShops:paginate', data)
        return data
      })
    },
    next($state) {
      this.fetch().then(payload => {
        if (!payload || !payload.pages) return $state.complete()

        this.paginate.page++
        let { docs, ...rest } = payload

        if (!docs.length) this.noUnits = true

        if (docs) {
          if (payload.page > this.item.page) this.item.docs.push(...docs)
          if (payload.page < this.item.page) this.item.docs.unshift(...docs)
        }

        Object.entries(rest).forEach(([key, value]) => {
          this.item[key] = value
        })

        $state.loaded()
        if (this.isLast) $state.complete()
      })
    },
    async resolveSelected() {
      const { allSelected, excludedIds, items } = this.selected
      if (!allSelected) return items

      let docs = []
      let page = 1
      let pages = 1

      while (page <= pages) {
        const payload = await API.get({ paginate: { page, limit: 100 }, filter: this.query }, { query: 'ProjectShops' })
        if (!payload) break

        docs.push(...(payload.docs || []))
        pages = payload.pages || 0
        page++
      }

      return docs.filter(e => !excludedIds.includes(e.id))
    },
    openSetDates() {
      return (this.promises.setDates = this.resolveSelected().then(residentials => {
        this.setDatesResidentials = residentials
        this.setDates = true
      }))
    },
    startShopContracts() {
      return (this.promises.startShops = this.resolveSelected().then(residentials => {
        let ids = residentials.filter(e => this.$path('shop.contract.data.applicant', e)).map(e => e.shop.contract.id)
        const notify = () => {
          if (ids.length < residentials.length)
            Bus.$emit('notification', { content: this.$t('noApplicantShopContractTooltip') })
        }
        if (!ids.length) return notify()

        return this.updateShopContracts(ids).then(notify)
      }))
    },
    updateShopContracts(ids) {
      return fetch(`/api/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation ($ids: [ID!], $data: ShopContractUpdate!) {
              ShopContractsUpdate(ids: $ids, data: $data) {
                id
              }
            }
          `,
          variables: {
            ids,
            data: {
              status: 'unsigned',
            },
          },
        }),
      })
    },
    onUpdateDone() {
      this.setDates = false
      this.reset()
    },
    reset() {
      if (this.$refs.table) this.$refs.table.reset()
      this.selected = createSelection()
      this.setDatesResidentials = []
      this.paginate.page = 1
      this.item.docs = []
      this.item.page = null
      this.item.pages = null
      this.item.total = null
      this.infiniteId++
    },
    getOwners(contract) {
      if (!this.$path('fullSignerName', contract)) return []
      const names = this.$path('fullSignerName', contract)
        .split('/')
        .map(k => k.trim())

      return [
        this.$path('data.applicant.name', contract),
        this.$path('data.hasCoApplicant', contract) ? this.$path('data.coApplicant.name', contract) : false,
      ]
        .filter(Boolean)
        .map((k, i) => ({ name: names[i], src: `/api/avatar/${Initials(k)}` }))
    },
    getTotal(item) {
      const total = this.$path('shop.total.gross', item)
      const status = this.$path('contract.status', item)
      if (!total && !['unsigned', 'signed', 'accepted'].includes(status)) return
      return Currency(total, { currency: this.project.metadata.currency })
    },
  },

  components: {
    Card,
    InfiniteLoading,
    SignerStatus,
    DeliiveryLogsShopClick,
    SetDates,
    DataTable,
    Badge,
    Avatar,
    EmptyState,
    Actions,
    StatusFilter,
  },
}
</script>

<style lang="scss">
.shops-list {
  overflow: hidden;

  & > .card__content {
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  &__scroller {
    height: 563px;
    padding: 0 var(--card-spacing);
    overflow-y: auto;
    overflow-x: auto;

    @include scrollShadow();

    @include respond-below('phone') {
      max-width: calc(100vw - 8rem);
    }
  }

  .residential-list-status-filter {
    padding: var(--card-spacing) calc(var(--card-spacing) * 1.5) calc(var(--card-spacing) / 4) var(--card-spacing);
  }

  .infinite-loading-spinner {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    &--text {
      opacity: 0.7;
    }
    &--spinner {
      color: hsla(var(--primary-h), var(--primary-s), var(--primary-l), 0.5);
    }
  }

  .infinite-status-prompt {
    margin-bottom: var(--card-spacing);
  }
}
</style>
